import type { Fixture, SidecarMap3 } from '../../../../dorian-shared/types/episodeScript';
import type { LocationMedia } from '../read/readBookNames';
import type { TitleRead } from '../read/readBookMedia';
import { sceneOfBranch } from './compactGraph';
import type { GraphNode } from './compactGraph';
import { IDENTITY_ALIGNMENT } from './lineAlignment';
import type { LineAlignment } from './lineAlignment';

/**
 * Basil v0.2, P3 — the episode's media, stated as facts in the margin.
 *
 * Three rules run through every line of this file.
 *
 * 1. **The labels are READ-ONLY margin content.** Nothing here is a script line, nothing here is
 *    parsed, and nothing here ever reaches the textarea's value. The grammar does not change in
 *    this round, and a label is not a command.
 * 2. **A failed lookup is never shown as absence.** "Sound attached" means the attachment is known
 *    and the title is not. "Sound details unavailable" means the lookup could not answer. Saying
 *    nothing at all means there is nothing attached — and it is said only when that is known.
 * 3. **Kitchen is a location's title, not a picture's name.** The two are separate labels because
 *    they are separate things: a location is where the scene takes place, and the background is
 *    the image behind it, which has a word of its own.
 */

/** Which thing a label is about. */
export type MediaLabelKind = 'location' | 'picture' | 'music' | 'sound' | 'video' | 'media';

/** One fact in the margin, and the scene its action opens. */
export interface MediaLabel {
  kind: MediaLabelKind;
  /** the whole sentence the writer reads, e.g. `Location: Kitchen` or `Sound details unavailable`. */
  text: string;
  /** the node the label belongs to, for the protected exit. */
  branchId: string | null;
  /** the scene as the script spells it, for "Open Hall for editing". */
  sceneName: string | null;
}

/** The word each kind leads with. */
const WORD: Readonly<Record<MediaLabelKind, string>> = Object.freeze({
  location: 'Location',
  picture: 'Picture',
  music: 'Music',
  sound: 'Sound',
  video: 'Video',
  media: 'Picture and music',
});

/** `Sound: magic bell` — the title is known. */
export function titled(kind: MediaLabelKind, title: string): string {
  return `${WORD[kind]}: ${title}`;
}

/** `Sound attached` — something IS attached and this surface has no word for it. */
export function attached(kind: MediaLabelKind): string {
  return `${WORD[kind]} attached`;
}

/** `Sound details unavailable` — the lookup could not answer. Never read as "there is none". */
export function unavailable(kind: MediaLabelKind): string {
  return `${WORD[kind]} details unavailable`;
}

/**
 * T17-07: a scene whose location row the list did not carry.
 *
 * The scene names a location, so it HAS one; what the surface does not have is that row's
 * background and its music. Saying nothing about them read as "this scene has no picture and no
 * music", which was never established. This says what is true: they could not be looked up.
 */
export const PICTURE_AND_MUSIC_UNAVAILABLE = `${unavailable('media')}.`;

/**
 * One label for one attachment, given what the lookup could say about it.
 *
 * @param kind which thing it is
 * @param id the attachment's id, as the episode read gave it; null means nothing is attached
 * @param lookup the read that would carry its title
 */
export function labelFor(kind: MediaLabelKind, id: string | null, lookup: TitleRead): string | null {
  if (id == null || id === '') {
    return null;
  }
  // T17-07: the read as a whole could not answer, or this ONE id could not. Either way this
  // attachment's title is unknown; a sibling that answered keeps its own title.
  if (!lookup.ok || (lookup.missing != null && lookup.missing[id] === true)) {
    return unavailable(kind);
  }
  const title = lookup.titles[id];

  return title ? titled(kind, title) : attached(kind);
}

/** What the margin is built from. Everything is optional, because every read of it fails open. */
export interface MediaLabelInput {
  map: SidecarMap3 | null | undefined;
  /** the episode as it was READ: the steps carry their own audio and video. */
  fixture: Fixture | null | undefined;
  /** the book's word for each location id (the v0.1.1 read). */
  locationNames: Record<string, string>;
  /** each location's `backgroundId` and `audioId` (the same read). */
  locationMedia: Record<string, LocationMedia>;
  /** whether the locations list answered at all. */
  locationsRead: boolean;
  /** the book's sounds and music, by audio id. */
  audio: TitleRead;
  /** the labels of the backgrounds this episode uses, by background id. */
  backgrounds: TitleRead;
  /** the scene heading each node has in the script, keyed by node id. */
  headings: Map<string, string>;
  /** the compact graph, for a node whose heading the script does not spell. */
  graph: GraphNode[];
  /**
   * T17-04: where each of the map's lines sits in the script the writer is looking at.
   *
   * The labels are keyed by the line they are rendered beside, and that is a line of the CURRENT
   * text. Absent, the map's own line numbers are used, which is only correct while the writer has
   * added and removed nothing.
   */
  align?: LineAlignment;
}

const stepsOf = (fixture: Fixture | null | undefined, branchId: string) => {
  const branch = (fixture?.branches ?? []).find((one) => String(one.id) === branchId);

  return branch ? (branch.steps ?? []) : [];
};

const branchOf = (fixture: Fixture | null | undefined, branchId: string) => (fixture?.branches ?? [])
  .find((one) => String(one.id) === branchId);

/**
 * Every label in the episode, keyed by the 1-based script line it sits beside.
 *
 * A scene heading carries its location, its picture and its music. A line carries its own sound or
 * music and its video. A line with nothing attached has no row, which is what keeps the margin a
 * margin rather than a second column of the script.
 */
export function mediaLabelsByLine(input: MediaLabelInput): Map<number, MediaLabel[]> {
  const {
    map, fixture, locationNames, locationMedia, locationsRead, audio, backgrounds, headings, graph,
  } = input;
  const align = input.align ?? IDENTITY_ALIGNMENT;
  const byLine = new Map<number, MediaLabel[]>();

  const put = (line: number, label: MediaLabel): void => {
    byLine.set(line, [...(byLine.get(line) ?? []), label]);
  };

  (map?.blocks ?? []).forEach((block) => {
    const branchId = block.branchId == null ? null : String(block.branchId);
    // The ATTACHMENT facts come from the original read, where they were established; only the
    // POSITION is asked of the script as it stands. Where the alignment cannot say where this
    // block went, the label is withheld: a label beside the wrong line is a false statement about
    // the writer's episode, and saying nothing is the only honest alternative.
    const line = Number(align.current(Number(block.line)));

    if (!branchId || !Number.isFinite(line) || line <= 0) {
      return;
    }
    const sceneName = sceneOfBranch(headings, graph, branchId);
    const of = (kind: MediaLabelKind, text: string): MediaLabel => ({
      kind, text, branchId, sceneName,
    });

    if (block.kind === 'scene') {
      const branch = branchOf(fixture, branchId);
      const locationId = branch && branch.locationId != null ? String(branch.locationId) : null;

      if (locationId == null) {
        return;
      }
      if (!locationsRead) {
        // The scene HAS a location and the list that names it could not answer. Saying nothing
        // here would read as "this scene has no location", which is the one thing it is not.
        put(line, of('location', unavailable('location')));

        return;
      }
      const name = locationNames[locationId];

      put(line, of('location', name ? titled('location', name) : attached('location')));

      const media = locationMedia[locationId];

      if (!media) {
        // T17-07: the list answered but carried no row for this location, so whether it has a
        // picture or music was never established. That is not the same as having neither.
        put(line, of('media', PICTURE_AND_MUSIC_UNAVAILABLE));

        return;
      }
      const picture = labelFor('picture', media.backgroundId, backgrounds);

      if (picture) {
        put(line, of('picture', picture));
      }
      const music = labelFor('music', media.audioId, audio);

      if (music) {
        put(line, of('music', music));
      }

      return;
    }

    if (block.stepId == null) {
      return;
    }
    const step = stepsOf(fixture, branchId).find((one) => String(one.id) === String(block.stepId));

    if (!step) {
      return;
    }
    (step.stepAudios ?? []).forEach((entry) => {
      const audioId = entry && entry.audioId != null ? String(entry.audioId) : null;
      // The engine's own word for the row decides which label it wears: a line can carry music as
      // well as an effect, and calling a piece of music a sound would be a small lie in the margin.
      const kind: MediaLabelKind = entry && entry.type === 'music' ? 'music' : 'sound';
      const label = labelFor(kind, audioId, audio);

      if (label) {
        put(line, of(kind, label));
      }
    });

    if (step.video) {
      // The video's title comes WITH the step, so there is no lookup here to fail: a video with no
      // title of its own is a video that is attached and unnamed.
      const title = typeof step.video.title === 'string' ? step.video.title.trim() : '';

      put(line, of('video', title === '' ? attached('video') : titled('video', title)));
    }
  });

  return byLine;
}

/** Every background id this episode's locations use, so the label read knows what to ask for. */
export function backgroundIdsOf(
  fixture: Fixture | null | undefined,
  locationMedia: Record<string, LocationMedia>,
): string[] {
  const ids = new Set<string>();

  (fixture?.branches ?? []).forEach((branch) => {
    if (branch.locationId == null) {
      return;
    }
    const media = locationMedia[String(branch.locationId)];

    if (media && media.backgroundId) {
      ids.add(media.backgroundId);
    }
  });

  return Array.from(ids).sort();
}
